// A snapshot persists only the report's *presentation* (series, colors, axes,
// labels, options) — never the raw chart data, which is always re-derivable from
// the reconciliation's executionResult. resultToDashboard inlines one point per
// datum into `chart.data`, so a scatter / spatial-categorical chart alone can
// push the DashboardConfig past the API's 1 MiB body limit (request_too_large).
// We strip `chart.data` before persisting and re-attach it from the freshly
// derived dashboard (by chart id) on read-back.

import type { DashboardConfig } from '@micronet/dashboard-kit';

/** A presentation-only copy of the config: every chart's `data` removed. */
export function stripChartData(config: DashboardConfig): DashboardConfig {
  return {
    ...config,
    charts: config.charts.map((chart) => {
      const presentation = { ...chart };
      delete presentation.data;
      return presentation;
    }),
  };
}

/**
 * Re-attach chart data to a stripped (persisted) config from a `source` dashboard
 * that still carries it — the freshly derived one — matching by chart id.
 *
 * Which charts EXIST is decided by `source`, i.e. by the current config; the
 * snapshot decides how the ones it names are presented. So:
 *
 *   • a stored chart present in `source` keeps its saved presentation and gets its
 *     data back;
 *   • a stored chart absent from `source` with no data of its own is dropped — its
 *     data is gone for good and keeping it would render a blank card under a stale
 *     title (that is what happened when `error` went from one chart to one per base
 *     and every saved report's error figure lost its id);
 *   • a chart `source` draws that the snapshot never had is ADDED. A
 *     presentation-only edit (`error_bases`) changes the chart set with no
 *     re-execution, so without this, reopening would silently undo an edit the user
 *     already made and saw applied.
 *
 * The cost of that last rule: here, a chart the user deliberately DELETED before
 * saving the snapshot is indistinguishable from an added one — both are "in the
 * derivation, not in the snapshot". The caller tells them apart by ownership:
 * openReconciliation drops every chart whose analysis the saved config no longer has
 * (store/useStore withoutDeletedCharts, Bug 2794). What still comes back is a
 * deleted FIGURE of an analysis that survives — one category of a split.
 *
 * KNOWN GAP, deliberately not closed here: that second rule loses the saved
 * presentation SILENTLY. A chart id can still leave the derivation while its figure
 * stays on screen — renaming a category at the source, or an intent's `idSuffix`
 * changing — and then the palette, card height and edited caption saved under the
 * old id are dropped while the figure returns with factory styling. Nothing tells
 * the user. Worse, `fetchViews` adopts the degraded config as the session's
 * dashboard (store/useStore), so the next "guardar snapshot" writes it over the
 * original: the loss is not just quiet, it is permanent. Reporting it needs a
 * channel this pure module does not have — the caller has one (`error` in the
 * store).
 *
 * What used to be the ROUTINE way in is gone: the fan-out's figures were numbered
 * by draw order, so an empty category renumbered the ones after it on every run.
 * They are named by category now.
 */
/**
 * Whether a stored presentation actually holds charts.
 *
 * `working_dashboard_config` and a view's `dashboard_config` both default to
 * `'{}'::jsonb`, so "persisted" and "has something to show" are different
 * questions. Callers that restore a presentation must ask this one first, or
 * they replace a live dashboard with an empty shell.
 */
export function hasCharts(stored: DashboardConfig | undefined | null): stored is DashboardConfig {
  return (stored?.charts?.length ?? 0) > 0;
}

type Chart = DashboardConfig['charts'][number];
type Hlines = NonNullable<Chart['limits']>['hlines'];
type Vlines = NonNullable<Chart['limits']>['vlines'];

/** Whether two charts draw the same series (same `dataKey` set, any order). A
 *  chart without series has nothing to disagree about. */
function sameSeries(a: Chart, b: Chart): boolean {
  if (!a.series || !b.series) return true;
  const keys = new Set(b.series.map((s) => s.dataKey));
  const aKeys = new Set(a.series.map((s) => s.dataKey));
  return aKeys.size === keys.size && [...aKeys].every((k) => keys.has(k));
}

/**
 * `fallback` (optional) is a second derivation of the same result, tried for a
 * stored chart that `source` would replace: when it still draws the stored
 * series, its rows are attached and the stored styling stays. `selectView` passes
 * the EXECUTED derivation so a sealed view published under the executed measures
 * keeps showing exactly what was sent (SPEC/2515, round 3).
 */
export function reattachChartData(
  stored: DashboardConfig,
  source: DashboardConfig,
  fallback?: DashboardConfig
): DashboardConfig {
  // `charts` can legitimately be absent: working_dashboard_config defaults to
  // '{}'::jsonb, so a reconciliation published without its shape ever being
  // persisted (a direct POST /publish, or one with no dashboard) mints view #1
  // with an empty object. Reading `.charts` off it threw inside selectView's
  // click handler, which React swallows — the chip simply did not respond, with
  // nothing on screen or in the log to say why.
  const storedCharts = stored?.charts ?? [];
  const sourceCharts = source?.charts ?? [];
  const dataById = new Map(sourceCharts.map((c) => [c.id, c.data]));
  const sourceById = new Map(sourceCharts.map((c) => [c.id, c]));
  const storedIds = new Set(storedCharts.map((c) => c.id));
  // A stored chart whose series differ from the ones the source derives is stale,
  // not styled: a presentation-only edit (`plot_measures`, SPEC/2515) keeps the
  // chart id but swaps its series. Series the source lost would read keys the rows
  // lack, and series it gained would never be drawn. Either way the source chart
  // takes its place. The presentation editor cannot add or remove series, so a
  // different set (a subset included) is never the user's own edit.
  const fallbackById = new Map((fallback?.charts ?? []).map((c) => [c.id, c]));
  // A stored chart with no `subtitle` key had a derived one, which is not persisted
  // (withoutDerivedSubtitles): the derivation its data comes from supplies it.
  const withSubtitleOf = (c: Chart, from: Chart | undefined): Chart =>
    'subtitle' in c || from?.subtitle === undefined ? c : { ...c, subtitle: from.subtitle };
  const reattach = (c: Chart): Chart => {
    const fresh = sourceById.get(c.id);
    if (!fresh || sameSeries(c, fresh)) {
      return withSubtitleOf({ ...c, data: dataById.get(c.id) ?? c.data ?? [] }, fresh);
    }
    const prior = fallbackById.get(c.id);
    if (prior && sameSeries(c, prior)) {
      return withSubtitleOf({ ...c, data: prior.data ?? [] }, prior);
    }
    return fresh;
  };
  return {
    // `source` primero: con un `stored` vacío o parcial, `id` y `title` (ambos
    // obligatorios en DashboardConfig) venían del objeto vacío y desaparecían —
    // y savePresentation los persistía así, volviendo permanente la pérdida.
    ...source,
    ...stored,
    charts: [
      ...storedCharts
        .filter((c) => dataById.has(c.id) || (c.data?.length ?? 0) > 0)
        .map(reattach),
      ...sourceCharts.filter((c) => !storedIds.has(c.id)),
    ],
  };
}

/**
 * Apply the chart-set change a CONFIG edit implies to the dashboard the user is
 * currently looking at, without disturbing anything else on it.
 *
 * `before` and `after` are what the SAME executed result derives under the config
 * as it was and as it now is. Their difference is exactly what the edit created or
 * withdrew — a presentation-only field such as `error_bases` changes which charts
 * exist without changing a single number, so nothing needs re-running and the user
 * expects the figure to appear (or go) on save.
 *
 * Charts the user deleted by hand are absent from `shown` while present in BOTH
 * derivations, so they are in neither set and stay deleted. That is the whole
 * reason this compares two derivations instead of merging `after` into `shown`.
 *
 * That holds only while the edit leaves every chart id where it was, and a
 * STRUCTURAL edit does not: delete an analysis and the saved config has fewer
 * entries than the stored result has envelopes, so the leftovers fall back to a
 * guessed identity and draw charts under ids `before` never had. They look exactly
 * like something the edit created — which is how pressing Guardar after a delete
 * brought the figure back "without configuration". `accountedFor` is the caller's
 * answer to "does the new config actually own this chart?", and only additions are
 * asked: a withdrawal is a withdrawal either way.
 *
 * Applied against `shown`, not against `before`, so it is idempotent: a chart the
 * edit creates and `shown` already carries is not added a second time. `before` is
 * the EXECUTED derivation and a save does not re-execute, so every save until the
 * next run recomputes the same delta — and execute() saves before it dispatches,
 * so Guardar · Ejecutar applies it twice.
 *
 * Returns `shown` itself when the edit changed no chart ON IT, so the caller can
 * skip the state write on an ordinary save.
 */
export function applyChartSetChange(
  shown: DashboardConfig,
  before: DashboardConfig,
  after: DashboardConfig,
  accountedFor: (chartId: string) => boolean = () => true
): DashboardConfig {
  const beforeIds = new Set(before.charts.map((c) => c.id));
  const afterIds = new Set(after.charts.map((c) => c.id));
  const shownIds = new Set(shown.charts.map((c) => c.id));
  const added = after.charts.filter(
    (c) => !beforeIds.has(c.id) && !shownIds.has(c.id) && accountedFor(c.id)
  );
  const removed = new Set(before.charts.filter((c) => !afterIds.has(c.id)).map((c) => c.id));
  // Asked of `shown`: a withdrawal already applied is no change either.
  if (added.length === 0 && !shown.charts.some((c) => removed.has(c.id))) return shown;
  return {
    ...shown,
    charts: [...shown.charts.filter((c) => !removed.has(c.id)), ...added],
  };
}

/**
 * The other half of a presentation-only edit: `plot_measures` (SPEC/2515) keeps
 * every chart id and changes which series a chart draws, so `applyChartSetChange`
 * sees nothing. Each chart on screen whose series differ between the two
 * derivations is replaced, at the same position, by its `after` version. Its
 * styling goes with it, because the series it styled no longer exist. Charts the
 * user deleted are not in `shown` and stay deleted.
 *
 * Returns `shown` itself when no chart changed.
 */
export function applyChartRedraw(
  shown: DashboardConfig,
  before: DashboardConfig,
  after: DashboardConfig
): DashboardConfig {
  const beforeById = new Map(before.charts.map((c) => [c.id, c]));
  const redrawn = new Map(
    after.charts
      .filter((c) => {
        const prev = beforeById.get(c.id);
        return prev !== undefined && !sameSeries(prev, c);
      })
      .map((c) => [c.id, c])
  );
  if (!shown.charts.some((c) => redrawn.has(c.id))) return shown;
  return { ...shown, charts: shown.charts.map((c) => redrawn.get(c.id) ?? c) };
}

/**
 * The third kind of presentation-only edit: `error_tolerance` (SPEC/2423) keeps
 * every chart id and every series and moves only the chart's derived reference
 * lines (`limits.hlines`), so neither function above sees it. On each chart on
 * screen whose derived `hlines` differ between the two derivations, just those
 * lines are replaced (see {@link withReferenceLines}); the rest of the chart,
 * styling included, stays. Any other `limits` key is kept too — conciliatron's
 * presentation editor never exposes limits, so today there is none to keep, but
 * the lines are the only part this edit owns. Charts the user deleted are not in
 * `shown` and stay deleted.
 *
 * Returns `shown` itself when no chart's lines moved.
 */
export function applyChartLimits(
  shown: DashboardConfig,
  before: DashboardConfig,
  after: DashboardConfig
): DashboardConfig {
  const hlinesOf = (c: Chart | undefined) => c?.limits?.hlines;
  const beforeById = new Map(before.charts.map((c) => [c.id, c]));
  const moved = new Map(
    after.charts
      .filter((c) => {
        const prev = beforeById.get(c.id);
        return prev !== undefined && JSON.stringify(hlinesOf(prev)) !== JSON.stringify(hlinesOf(c));
      })
      .map((c) => [c.id, hlinesOf(c)])
  );
  if (!shown.charts.some((c) => moved.has(c.id))) return shown;
  return {
    ...shown,
    charts: shown.charts.map((c) => (moved.has(c.id) ? withReferenceLines(c, moved.get(c.id)) : c)),
  };
}

/**
 * `shown` with each chart's reference lines (`limits.hlines` and `limits.vlines`)
 * taken from the same chart in `derived`; everything else, styling included,
 * stays. For reopening a persisted shape: conciliatron's presentation editor never
 * exposes reference lines, so a stored one is only ever a past derivation's, and
 * it can be stale — a shape saved while the `error` tolerance sat at ±t/100
 * (SPEC/2620) kept its ±0.1 lines, and Guardar with the same tolerance never moves
 * them ({@link applyChartLimits} diffs two current derivations). The scatter's
 * cut-off cross-hair is one of each, and a run at another cut-off left the
 * vertical one behind (Bug 2753). Charts `derived` does not draw are left alone.
 *
 * Returns `shown` itself when no chart's lines differ.
 */
export function withDerivedReferenceLines(
  shown: DashboardConfig,
  derived: DashboardConfig
): DashboardConfig {
  const derivedById = new Map((derived.charts ?? []).map((c) => [c.id, c]));
  let changed = false;
  const charts = (shown.charts ?? []).map((c) => {
    const fresh = derivedById.get(c.id);
    if (!fresh) return c;
    const next = withVerticalLines(
      withReferenceLines(c, fresh.limits?.hlines),
      fresh.limits?.vlines
    );
    if (next !== c) changed = true;
    return next;
  });
  return changed ? { ...shown, charts } : shown;
}

/**
 * `chart` with its derived reference lines (`limits.hlines`) replaced by `hlines`,
 * everything else untouched. An emptied `limits` is dropped rather than left as
 * `{}`, so the chart has the shape it would have been derived with.
 */
export function withReferenceLines<C extends Chart>(chart: C, hlines: Hlines | undefined): C {
  if (JSON.stringify(chart.limits?.hlines) === JSON.stringify(hlines)) return chart;
  const limits = { ...(chart.limits ?? {}) };
  if (hlines) limits.hlines = hlines;
  else delete limits.hlines;
  const next: C = { ...chart, limits };
  if (Object.keys(limits).length === 0) delete next.limits;
  return next;
}

/** {@link withReferenceLines} for the vertical lines (`limits.vlines`). */
function withVerticalLines<C extends Chart>(chart: C, vlines: Vlines | undefined): C {
  if (JSON.stringify(chart.limits?.vlines) === JSON.stringify(vlines)) return chart;
  const limits = { ...(chart.limits ?? {}) };
  if (vlines) limits.vlines = vlines;
  else delete limits.vlines;
  const next: C = { ...chart, limits };
  if (Object.keys(limits).length === 0) delete next.limits;
  return next;
}

/**
 * A presentation to persist, minus every subtitle that is just a derivation's.
 *
 * A derived subtitle states the run's parameters — the compared columns, the
 * filters, the `error` cut-off (SPEC/2423). Persisted, it goes stale on the next
 * run (Ejecutar saves the shape BEFORE running, and nothing saves it after), and
 * on reopen the stored copy won over the fresh one: a report could state a cut-off
 * its numbers were not computed with. So a subtitle equal to one of `derivations`
 * (the dashboard's before/after derivations of the same result) is dropped, and
 * {@link reattachChartData} supplies the current one. One the user typed in the
 * presentation editor differs and is kept, cleared-to-empty included.
 */
export function withoutDerivedSubtitles(
  shape: DashboardConfig,
  derivations: DashboardConfig[]
): DashboardConfig {
  const derived = (id: string) =>
    derivations.map((d) => d.charts.find((c) => c.id === id)?.subtitle);
  return {
    ...shape,
    charts: shape.charts.map((c) => {
      if (c.subtitle === undefined || !derived(c.id).includes(c.subtitle)) return c;
      const next = { ...c };
      delete next.subtitle;
      return next;
    }),
  };
}
